import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { isSubOrganization } from "../utils/customerHelpers";
import {
  readCustomerLogoId,
  removeCustomerLogo,
  uploadCustomerLogo,
  useCustomerLogo,
} from "./CustomerLogoMark";

const ALLOWED_LOGO_EXT = /\.(png|jpe?g)$/i;
const LOGO_FILENAME_REGEX = /^[a-zA-Z0-9._-]+$/;

const formatCustomerCreatedDate = (dateString) => {
  if (!dateString) return null;
  const localDate = new Date(String(dateString).replace(/-/g, "/"));
  if (Number.isNaN(localDate.getTime())) {
    const trimmed = String(dateString).trim();
    return trimmed || null;
  }
  return localDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ py: 1.5 }}>
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "#64748b",
        fontSize: "0.75rem",
        fontWeight: 600,
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        color: value ? "#1e293b" : "#94a3b8",
        fontSize: "0.875rem",
        fontStyle: value ? "normal" : "italic",
        wordBreak: "break-word",
      }}
    >
      {value || "Not available"}
    </Typography>
  </Box>
);

const CustomerDetailsDialog = ({ open, onClose, customer, canManageLogo = false }) => {
  const orgCode = customer?.orgcode || "";
  const logoId = readCustomerLogoId(customer);
  const isSubOrg = isSubOrganization(customer);
  const logo = useCustomerLogo(orgCode, logoId, open && isSubOrg && Boolean(orgCode));
  const fileInputRef = useRef(null);
  const [logoAction, setLogoAction] = useState("");
  const [logoMessage, setLogoMessage] = useState("");
  const [logoMessageSeverity, setLogoMessageSeverity] = useState("error");
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setLogoMessage("");
    setImageFailed(false);
    setLogoAction("");
  }, [orgCode, open]);

  useEffect(() => {
    setImageFailed(false);
  }, [logo.url]);

  const handleLogoFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !orgCode) return;

    const name = file.name || "";
    if (!ALLOWED_LOGO_EXT.test(name)) {
      setLogoMessageSeverity("error");
      setLogoMessage("Logo must be PNG or JPEG.");
      return;
    }
    if (file.size === 0) {
      setLogoMessageSeverity("error");
      setLogoMessage("File is empty.");
      return;
    }
    if (name.length > 255 || !LOGO_FILENAME_REGEX.test(name)) {
      setLogoMessageSeverity("error");
      setLogoMessage(
        "Invalid file name. Use only letters, numbers, dots, hyphens, and underscores. No spaces or special characters."
      );
      return;
    }

    setLogoAction("upload");
    setLogoMessage("");
    setImageFailed(false);
    try {
      const result = await uploadCustomerLogo(orgCode, file);
      setLogoMessageSeverity(result.ok ? "success" : "error");
      setLogoMessage(
        result.ok
          ? result.logo?.url
            ? "Logo updated."
            : result.logo?.scanStatus
              ? `Logo uploaded. Scan status: ${result.logo.scanStatus}.`
              : "Logo uploaded. It will appear when the scan finishes."
          : result.message
      );
    } catch (error) {
      setLogoMessageSeverity("error");
      setLogoMessage(error?.message || "Logo upload failed.");
    } finally {
      setLogoAction("");
    }
  };

  const handleRemoveLogo = async () => {
    if (!orgCode) return;
    setLogoAction("remove");
    setLogoMessage("");
    try {
      const result = await removeCustomerLogo(orgCode);
      setLogoMessageSeverity(result.ok ? "success" : "error");
      setLogoMessage(result.ok ? "Logo removed." : result.message);
      if (result.ok) setImageFailed(false);
    } catch (error) {
      setLogoMessageSeverity("error");
      setLogoMessage(error?.message || "Failed to remove logo.");
    } finally {
      setLogoAction("");
    }
  };

  return (
  <Modal
    open={open && !!customer}
    onClose={onClose}
    aria-labelledby="customer-details-dialog-title"
  >
    <Paper
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: { xs: "95%", sm: 440 },
        maxWidth: 440,
        bgcolor: "background.paper",
        borderRadius: "8px",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        border: "1px solid #e2e8f0",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#F5F5F5",
          padding: "16px 24px",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
        }}
      >
        <Typography
          id="customer-details-dialog-title"
          variant="h6"
          sx={{
            fontWeight: "bold",
            color: "#000",
            fontSize: "1.125rem",
          }}
        >
          {customer?.customer_name || "Customer"}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            color: "#000",
            "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, py: 2 }}>
        <DetailRow
          label="Created"
          value={formatCustomerCreatedDate(customer?.created_date)}
        />
        <Divider sx={{ borderColor: "#e2e8f0" }} />
        <DetailRow
          label="Email"
          value={(customer?.orgprimaryemail ?? "").toString().trim() || null}
        />
        <Divider sx={{ borderColor: "#e2e8f0" }} />
        <DetailRow
          label="Phone"
          value={
            (customer?.phonenumber ?? customer?.phoneNumber ?? "").toString().trim() ||
            null
          }
        />
        {isSubOrg && (
          <>
            <Divider sx={{ borderColor: "#e2e8f0" }} />
            <Box sx={{ py: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#64748b",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Logo
              </Typography>
              {(logo.phase === "loading" || logo.phase === "idle") && !logo.url && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" sx={{ color: "#64748b" }}>
                    Loading logo…
                  </Typography>
                </Box>
              )}
              {logo.url && !imageFailed && (
                <Box
                  component="img"
                  src={logo.url}
                  alt=""
                  onError={() => setImageFailed(true)}
                  sx={{
                    display: "block",
                    width: 48,
                    height: 48,
                    objectFit: "contain",
                    borderRadius: "6px",
                    border: "1px solid #e2e8f0",
                    backgroundColor: "#fff",
                    mb: 1,
                  }}
                />
              )}
              {!logo.url && logo.phase !== "loading" && logo.phase !== "idle" && !logo.error && (
                <Typography variant="body2" sx={{ color: "#94a3b8", fontSize: "0.875rem", fontStyle: "italic", mb: 1 }}>
                  No logo
                </Typography>
              )}
              {logo.scanStatus && (
                <Typography variant="caption" sx={{ display: "block", color: "#64748b", mb: 1 }}>
                  Scan status: {logo.scanStatus}
                </Typography>
              )}
              {logo.error && logo.message && (
                <Typography variant="caption" sx={{ display: "block", color: "#dc2626", mb: 1 }}>
                  {logo.message}
                </Typography>
              )}
              {canManageLogo && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    hidden
                    onChange={handleLogoFile}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={Boolean(logoAction)}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.8125rem", fontWeight: 600 }}
                  >
                    {logoAction === "upload" ? "Uploading…" : logo.url ? "Replace logo" : "Upload logo"}
                  </Button>
                  {logo.url && (
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      disabled={Boolean(logoAction)}
                      onClick={handleRemoveLogo}
                      sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.8125rem", fontWeight: 600 }}
                    >
                      {logoAction === "remove" ? "Removing…" : "Remove logo"}
                    </Button>
                  )}
                </Box>
              )}
              {logoMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1,
                    color: logoMessageSeverity === "success" ? "#059669" : "#dc2626",
                  }}
                >
                  {logoMessage}
                </Typography>
              )}
            </Box>
          </>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Paper>
  </Modal>
  );
};

CustomerDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  canManageLogo: PropTypes.bool,
};

export default CustomerDetailsDialog;
